import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import { PRIMARY_NAV } from './navConfig';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { cx } from '../../lib/format';
import { useMenu, isExternalUrl } from '../../lib/useMenu';

/** A nav link that uses a plain <a> for external URLs and router Link otherwise. */
function NavItemLink({ url, target, className, onClick, children }) {
  if (isExternalUrl(url)) {
    return (
      <a
        href={url}
        target={target || '_self'}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={url} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { isAuthenticated, isMember, isAdmin, user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { items: menuItems } = useMenu('header');

  // Lock body scroll while the mobile drawer is open (mirrors Lightbox behavior).
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  async function handleLogout() {
    try {
      await logout();
      toast.success('Signed out.');
      navigate('/');
    } catch {
      toast.error('Could not sign out.');
    }
  }

  const dashboardLink = isAdmin ? '/admin' : isMember ? '/member' : null;

  return (
    <header className="sticky top-0 z-40 border-b border-cream-300 bg-surface-card/90 backdrop-blur">
      {/* Top utility bar */}
      <div className="bg-maroon-800 text-cream-100">
        <div className="container-page flex h-9 items-center justify-between text-xs">
          <span className="hidden sm:block">Empowering women through service &amp; advocacy</span>
          <div className="flex items-center gap-4">
            <a
              href="https://www.zonta.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium hover:text-gold-400"
            >
              Zonta International
              <span aria-hidden="true" className="text-[0.9em]">&#8599;</span>
            </a>
            <span aria-hidden="true" className="text-cream-100/30">|</span>
            {!isAuthenticated ? (
              <Link to="/login" className="font-medium hover:text-gold-400">
                Member Login
              </Link>
            ) : (
              /* `user.name` is a `max:255` free-text field a member can set on
                 their own profile, and its first token can be 255 unbroken
                 characters. In this single-line utility bar the right treatment
                 is a clipped ellipsis, not wrapping: `truncate` also sets
                 `overflow: hidden`, which zeroes the flex item's automatic
                 minimum size so it can no longer widen the bar. No visible
                 change for a normal first name — those are far under 10rem. */
              <span className="max-w-[10rem] truncate font-medium">Welcome, {user?.name?.split(' ')[0]}</span>
            )}
          </div>
        </div>
      </div>

      <nav className="container-page flex h-16 items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav: static mega-menu ALWAYS renders; CMS header items appended after */}
        <ul className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpenMenu(null)}>
          {PRIMARY_NAV.map((item) => (
            <li
              key={item.label}
              className="relative"
              onMouseEnter={() => setOpenMenu(item.columns ? item.label : null)}
            >
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive ? 'text-maroon-700' : 'text-ink-soft hover:text-maroon-700'
                  )
                }
              >
                {item.label}
                {item.columns && <span className="text-[10px]">▾</span>}
              </NavLink>

              {item.columns && openMenu === item.label && (
                <div className="absolute left-0 top-full pt-2">
                  <div className="animate-fade-in min-w-[260px] rounded-2xl border border-cream-300 bg-surface-card p-4 shadow-lift">
                    {item.columns.map((col) => (
                      <div key={col.heading}>
                        <p className="eyebrow mb-2">
                          {col.heading}
                        </p>
                        <ul className="space-y-1">
                          {col.links.map((link) => (
                            <li key={link.to}>
                              <Link
                                to={link.to}
                                className="block rounded-lg px-3 py-2 text-sm text-ink-soft hover:bg-maroon-50 hover:text-maroon-700"
                              >
                                {link.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </li>
          ))}

          {/* Appended CMS-managed header items */}
          {menuItems.map((item) => (
            <li
              key={`cms-${item.id}`}
              className="relative"
              onMouseEnter={() => setOpenMenu(item.children.length ? `cms-${item.id}` : null)}
            >
              <NavItemLink
                url={item.url}
                target={item.target}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft transition-colors hover:text-maroon-700"
              >
                {item.label}
                {item.children.length > 0 && <span className="text-[10px]">▾</span>}
              </NavItemLink>

              {item.children.length > 0 && openMenu === `cms-${item.id}` && (
                <div className="absolute left-0 top-full pt-2">
                  <div className="animate-fade-in min-w-[220px] rounded-2xl border border-cream-300 bg-surface-card p-3 shadow-lift">
                    <ul className="space-y-1">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <NavItemLink
                            url={child.url}
                            target={child.target}
                            className="block rounded-lg px-3 py-2 text-sm text-ink-soft hover:bg-maroon-50 hover:text-maroon-700"
                          >
                            {child.label}
                          </NavItemLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Right actions */}
        <div className="hidden items-center gap-2 lg:flex">
          {dashboardLink && (
            <Link to={dashboardLink} className="btn-outline px-4 py-2 text-sm">
              Dashboard
            </Link>
          )}
          {isAuthenticated ? (
            <button onClick={handleLogout} className="btn-ghost text-sm">
              Logout
            </button>
          ) : (
            <Link to="/login" className="btn-primary !py-2 px-4 text-sm">
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden rounded-lg p-2 text-maroon-700"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>
      </nav>

      {/* Mobile drawer — portaled to <body> so `fixed` escapes the header's
          backdrop-filter containing block and covers the full viewport. */}
      {drawerOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-maroon-950/60" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] animate-fade-in overflow-y-auto bg-surface-card p-5 shadow-lift">
            <div className="mb-6 flex items-center justify-between">
              <Logo compact />
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu" className="p-2 text-ink-muted">
                ✕
              </button>
            </div>
            <ul className="space-y-1">
              {PRIMARY_NAV.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={() => setDrawerOpen(false)}
                    className="block rounded-lg px-3 py-2.5 font-medium text-ink-soft hover:bg-maroon-50 hover:text-maroon-700"
                  >
                    {item.label}
                  </Link>
                  {item.columns && (
                    <ul className="ml-3 border-l border-cream-300 pl-3">
                      {item.columns.flatMap((c) => c.links).map((link) => (
                        <li key={link.to}>
                          <Link
                            to={link.to}
                            onClick={() => setDrawerOpen(false)}
                            className="block rounded-lg px-3 py-2 text-sm text-ink-muted hover:text-maroon-700"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}

              {/* Appended CMS-managed header items */}
              {menuItems.map((item) => (
                <li key={`cms-${item.id}`}>
                  <NavItemLink
                    url={item.url}
                    target={item.target}
                    onClick={() => setDrawerOpen(false)}
                    className="block rounded-lg px-3 py-2.5 font-medium text-ink-soft hover:bg-maroon-50 hover:text-maroon-700"
                  >
                    {item.label}
                  </NavItemLink>
                  {item.children.length > 0 && (
                    <ul className="ml-3 border-l border-cream-300 pl-3">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <NavItemLink
                            url={child.url}
                            target={child.target}
                            onClick={() => setDrawerOpen(false)}
                            className="block rounded-lg px-3 py-2 text-sm text-ink-muted hover:text-maroon-700"
                          >
                            {child.label}
                          </NavItemLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2 border-t border-cream-300 pt-4">
              {dashboardLink && (
                <Link to={dashboardLink} onClick={() => setDrawerOpen(false)} className="btn-outline w-full">
                  Dashboard
                </Link>
              )}
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    handleLogout();
                  }}
                  className="btn-primary w-full"
                >
                  Logout
                </button>
              ) : (
                <Link to="/login" onClick={() => setDrawerOpen(false)} className="btn-primary w-full">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>,
          document.body
        )}
    </header>
  );
}
